import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSuperApp } from "./useSuperApp";
import { useAccountRole } from "../context/RoleContext";
import { getToken } from "../api/client";
import RoleSelectModal from "../components/RoleSelectModal";

// Asked once per signed-in session, then remembered for the rest of it. Every later
// "post" tap in that session goes straight to the chosen role's flow; signing in again
// asks again, because the person at the keyboard may not be the one who answered last
// time and because a role picked for one session is not a setting anyone went looking for.
//
// The answer is kept in sessionStorage keyed to the current token, which is what makes
// "again on the next login" work: a new login mints a new token, the key no longer
// matches, and the question comes back. (The stored role itself cannot stand in for this
// flag — it defaults to "jobseeker" and is also written from the server profile, so its
// presence says nothing about whether anyone was ever asked.)
const POST_ROLE_CHOSEN_KEY = "diil_post_role_chosen";

// The token's tail, not the token: this only has to change when the session does.
const sessionMark = () => (getToken() || "anon").slice(-16);

const hasChosenPostRole = () => {
  try {
    return sessionStorage.getItem(POST_ROLE_CHOSEN_KEY) === sessionMark();
  } catch {
    return false;
  }
};

const markPostRoleChosen = () => {
  try {
    sessionStorage.setItem(POST_ROLE_CHOSEN_KEY, sessionMark());
  } catch {
    /* private mode — the picker just shows again next time, which is harmless */
  }
};

// Single source of truth for where the shared "post" button (desktop header,
// mobile bottom nav) sends the user. Always requires login first: super-app
// users get silently re-authed, phone-OTP users are handed to the caller's
// own login modal via onNeedsLogin.
export function usePostAction() {
  const navigate = useNavigate();
  const { isAuthenticated, isSuperAppUser, retryAuth } = useSuperApp();
  const { accountRole } = useAccountRole();
  // onOpenEmployerPost carried through to the choice, so it reaches the
  // employer branch below once the caller has actually picked one.
  const [pending, setPending] = useState(null);

  // Where a chosen role sends the user. Employer callers on desktop (Header.jsx,
  // MyPosts.jsx) pass onOpenEmployerPost to open the wizard as a popup; mobile passes
  // nothing and navigates to the full page.
  const route = (role, onOpenEmployerPost) => {
    if (role === "jobseeker") {
      navigate("/job-group?compose=1");
    } else if (onOpenEmployerPost) {
      onOpenEmployerPost();
    } else {
      navigate("/employer/post");
    }
  };

  const goToPost = async (onNeedsLogin, onOpenEmployerPost) => {
    if (!isAuthenticated) {
      if (isSuperAppUser) {
        const success = await retryAuth();
        if (!success) return;
      } else {
        onNeedsLogin?.();
        return;
      }
    }

    // Already answered in this session: skip the picker and post as the chosen role.
    if (hasChosenPostRole()) {
      route(accountRole, onOpenEmployerPost);
      return;
    }

    setPending({ onOpenEmployerPost });
  };

  const roleChoiceModal = (
    <RoleSelectModal
      isOpen={!!pending}
      onClose={() => setPending(null)}
      onSelect={(role) => {
        const { onOpenEmployerPost } = pending || {};
        setPending(null);
        // RoleSelectModal already persisted the role via setAccountRole; just remember
        // that the question has been answered for the rest of this session.
        markPostRoleChosen();
        route(role, onOpenEmployerPost);
      }}
    />
  );

  return { goToPost, roleChoiceModal };
}

export default usePostAction;
